import { isSafeRepoUrl } from '@/lib/repoLinks'
import { RepoProviderIcon } from '@/shared/components/RepoProviderIcon'
import { Tooltip } from '@/shared/components/Tooltip'

/**
 * Renders the VCS source provenance of a module/state version as an icon-only
 * trigger with a hover popout (keeps the table column ~1 icon wide). The provider
 * logo IS the link: clicking it opens the exact commit; hovering shows a popout
 * with the repo, ref and full sha.
 *
 * The commit link is rendered ONLY when the server supplied a `commitUrl` on an
 * allowlisted host (checked via the shared isSafeRepoUrl) - the client never
 * builds the URL itself, so attacker-controlled claim values can't steer it. With
 * no safe URL the trigger is a non-navigating <button> (still hoverable for the
 * detail). Renders nothing when there is no repository.
 *
 * The trigger is a focusable element (<a>/<button>) so the Tooltip can clone its
 * ref + focus/keyboard handlers onto it (a bare <span> would be invisible to
 * keyboard/screen readers). Click/keydown propagation is stopped so activating the
 * icon never triggers the surrounding clickable row's navigation. The tooltip box
 * is pointer-events:none, so navigation lives on the icon trigger, not inside it.
 */

export interface SourceRepoLinkProps {
  provider: string | null
  repository: string | null
  // NOTE: named gitRef, NOT `ref` - `ref` is a reserved React prop (passing a
  // string to it throws "Function components cannot have string refs").
  gitRef: string | null
  refType?: string | null
  sha: string | null
  commitUrl?: string | null
  /** Surfaced in the popout header ("published"/"pushed from"). */
  verb?: 'published' | 'pushed'
}

export function SourceRepoLink({
  provider,
  repository,
  gitRef,
  refType,
  sha,
  commitUrl,
  verb = 'published',
}: SourceRepoLinkProps) {
  if (!repository) return null

  const shortSha = sha ? sha.slice(0, 8) : null
  const aria = `source: ${repository}${shortSha ? `@${shortSha}` : ''}`

  // Popout rows - only the ones with data. The ref row's LABEL conveys branch/tag,
  // so the VALUE is just the ref name (never "Branch: branch main").
  const rows: Array<[string, string]> = []
  rows.push(['Repository', repository])
  if (gitRef) rows.push([refType === 'tag' ? 'Tag' : refType === 'branch' ? 'Branch' : 'Ref', gitRef])
  if (sha) rows.push(['Commit', sha])

  const tip = (
    <span style={{ display: 'grid', gap: 6, textAlign: 'left', minWidth: 180 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}>
        <RepoProviderIcon provider={provider} size={14} />
        <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {verb} from
        </span>
      </span>
      <span style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
        {rows.map(([k, v]) => (
          <span key={k} style={{ display: 'contents' }}>
            <span style={{ color: 'var(--text3)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{k}</span>
            {/* nowrap so the full commit hash stays on one line; the box auto-sizes
                (Tooltip maxWidth="none") to the widest row instead of clipping. */}
            <span style={{ color: 'var(--text)', fontSize: '0.72rem', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{v}</span>
          </span>
        ))}
      </span>
    </span>
  )

  // Bare icon-button chrome: no background/border/padding, inherits colour, focusable.
  const triggerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: isSafeRepoUrl(commitUrl) ? 'pointer' : 'help',
    color: isSafeRepoUrl(commitUrl) ? 'var(--accent)' : 'var(--text2)',
  } as const

  const trigger = isSafeRepoUrl(commitUrl) ? (
    <a
      href={commitUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={aria}
      className="icon-trigger"
      style={{ ...triggerStyle, textDecoration: 'none' }}
      onClick={(e) => e.stopPropagation()}
    >
      <RepoProviderIcon provider={provider} size={18} />
    </a>
  ) : (
    <button
      type="button"
      aria-label={aria}
      className="icon-trigger"
      style={triggerStyle}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
    >
      <RepoProviderIcon provider={provider} size={18} />
    </button>
  )

  // maxWidth="none" lets the box auto-size to its content so the full commit hash
  // isn't clipped by the global 300px tooltip cap.
  // interactive so the user can move onto the box and select/copy the full sha.
  return <Tooltip text={tip} placement="top" maxWidth="none" interactive>{trigger}</Tooltip>
}
